<!-- eslint-disable max-len -->
<template>
  <div>
    <span v-if='project && project !== null && !(project.approvalStatus === "Cancelled" || project.approvalStatus === "Completed")'>
      <b-link v-if="isApprover && getFiscalYear(new Date()) === budgetYear.year && budgetYear.status === budgetStatus.Approved" @click="openAdjustmentModal()" class='add-link text-right'>
        <span>Adjust Annual Budget</span>
      </b-link>
    </span>
    <b-modal
      :id="`adjustmentModal-${budgetYear.year}`"
      title="Add Annual Adjustment"
      @hidden="cancelAdjustment"
      size="xl">
      <LoadingSpinner v-if='fundingSourcesLoading'
        message ='Loading Funding Source' size = '1x'>
      </LoadingSpinner>
      <template v-else>
        <b-overlay :show="pushingData">
          <b-alert
            :show="showAlertWarning"
            dismissible
            variant="danger"
          >
            {{ validationMessage }}
          </b-alert>
          <VueFormGenerator
            :schema="getAnnualBudgetIncreaseSchema()"
            :model="currentAdjustment"
            :options="{
            validateAfterLoad: false,
            validateAfterChanged: true,
            validateAsync: true
            }"
            @validated="onAdjustmentValidation"
            @model-updated="onAdjustmentUpdated"
            ref="annualBudgetIncreaseForm"
          />
        </b-overlay>
      </template>
      <template v-slot:modal-footer>
        <b-button
          size="sm"
          :disabled="pushingData"
          variant="danger"
          @click="$bvModal.hide(`adjustmentModal-${budgetYear.year}`)"
          class='management-plan-dialog-no'>
          Cancel
        </b-button>
        <b-button
          size="sm"
          :disabled="!validAdjustment || pushingData"
          variant="success"
          @click="onAddItemClicked(budgetYear.year)"
          class='management-plan-dialog-yes'>
          Add Item
        </b-button>
      </template>
    </b-modal>

    <b-modal title="Adjustment Confirmation" :id="`adjustmentConfirmationModal-${budgetYear.year}`" size='lg'>
      <div class="m-2">
        <div>Once you add an adjustment, you cannot change it.&nbsp;</div>
        <div>If you need to review the adjustment or make changes, press "Cancel".&nbsp;</div>
        <div>If you are sure your adjustment is correct, press "Increase Budget" to add the adjustment to this Annual Budget.</div>
      </div>
      <div class='container card p-3'>
        <div v-if='annualBudgetIncreaseDifference < 0' class='row p-1'>
          <div class="col text-danger">
            <span>WARNING:</span>
            <span>There are insufficient funds in this funding source to cover the requested change!</span>
          </div>
        </div>
        <div v-else class='row p-1'>
          <div class="col text-success">
            <span>Funds are sufficient in this funding source to cover the requested change.</span>
          </div>
        </div>
        <div class='row p-1'>
          <div class='col label-name'>
            <span>Target FY:</span>
          </div>
          <div class='col float-right'>
            <span class='ml-1'>{{budgetYear.year}}</span>
          </div>
        </div>
        <div class='row p-1'>
          <div class='col label-name'>
            <span>Funding Source:</span>
          </div>
          <div class='col'>
            <span class='ml-1'>{{fundingSource}}</span>
          </div>
        </div>
        <div class='row p-1'>
          <div class='col label-name'>
            <span>Total Approved Project Budget for</span>
            <span class='ml-1'>{{ fundingSource }}</span>
            <span class='ml-1'>for FY</span>
            <span class='ml-1'>{{budgetYear.year}}:</span>
          </div>
          <div class='col'>
            <span class='ml-1'>{{ fundingSourceApprovedProjectsBudget | toCurrency}}</span>
          </div>
        </div>
        <div class='row p-1'>
          <div class='col label-name'>
            <span>Encumbered from</span>
            <span class='ml-1'>{{fundingSource}}</span>
            <span class='ml-1'> for FY</span>
            <span class='ml-1'>{{budgetYear.year}}</span>
            <span class='ml-1'>(across all Projects):</span>
          </div>
          <div class='col'>
            <span class='ml-1'>{{ fundingSourceEncumberedTotal | toCurrency}}</span>
          </div>
        </div>
        <div class='row p-1'>
          <div class='col label-name'>
            <span>Remaining in</span>
            <span class='ml-1'>{{fundingSource}}</span>
            <span class='ml-1'>for FY</span>
            <span class='ml-1'>{{budgetYear.year}}</span>
            <span class='ml-1'>(across all Projects):</span>
          </div>
          <div class='col'>
            <span class='ml-1' v-bind:class='{ "text-danger": (fundingSourceRemainingTotal < 0)}'>{{ fundingSourceRemainingTotal | toCurrency}}</span>
          </div>
        </div>
        <div class='row p-1'>
          <div class='col label-name'>
            <span>Requested funding from</span>
            <span class='ml-1'>{{fundingSource}}:</span>
          </div>
          <div class='col'>
            <span class='ml-1'>{{ projectBudgetIncreaseTotal | toCurrency}}</span>
          </div>
        </div>
        <div class='row p-1'>
          <div class='col label-name'>
            <span>Difference:</span>
          </div>
          <div class='col'>
            <span class='ml-1' v-bind:class='{ "text-danger": (annualBudgetIncreaseDifference < 0)}'>{{ annualBudgetIncreaseDifference | toCurrency}}</span>
          </div>
        </div>
      </div>
      <template v-slot:modal-footer>
        <b-button
          size="sm"
          :disabled="pushingData"
          variant="danger"
          @click="$bvModal.hide(`adjustmentConfirmationModal-${budgetYear.year}`)"
          class='management-plan-dialog-no'>
          Cancel
        </b-button>
        <b-button
          :disabled="!validAdjustment || pushingData"
          size="sm"
          variant="success"
          @click="addAnnualBudgetIncrease(budgetYear)"
          class='management-plan-dialog-yes'>
          Increase Budget
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
<script>
/* eslint-disable max-len, padded-blocks */
import { mapState } from 'vuex';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';
import AlertManager from '@/mixins/AlertManager';
import FiscalYear from '@/mixins/fiscalYear';
import ProjectApprovalManager from '@/mixins/projectApprovalManager';
import ProjectApprovalMembershipManager from '@/mixins/projectApprovalMembershipManager';
import ProjectBudgetHelper from '@/mixins/projectBudgetHelper';
import ProjectBudgetHelperExpenditureSummary from '@/mixins/projectBudgetHelperExpenditureSummary';
import ProjectTotalsCalculator from '@/mixins/projectTotalsCalculator';

export default {
  name: 'AnnualBudgetIncreases',
  mixins: [
    AlertManager,
    FiscalYear,
    ProjectApprovalManager,
    ProjectApprovalMembershipManager,
    ProjectBudgetHelper,
    ProjectBudgetHelperExpenditureSummary,
    ProjectTotalsCalculator,
  ],
  components: {
    LoadingSpinner,
  },
  computed: {
    ...mapState('Project', {
      encumberedFundsForApprovedBudgets: (state) => state.encumberedFundsForApprovedBudgets,
      errorMessage: (state) => state.errorMessage,
      fundingSources: (state) => state.fundingSources,
      fundingSourceEncumberedFunds: (state) => state.fundingSourceEncumberedFunds,
      fundingSourceExpendedFunds: (state) => state.fundingSourceExpendedFunds,
      fundingSourcesLoading: (state) => state.fundingSourcesLoading,
      fundsPerFiscalYear: (state) => state.fundsPerFiscalYear.funds,
      project: (state) => state.project,
      projectBudget: (state) => state.projectBudget,
      projectBudgetYearSnapshots: (state) => state.projectBudgetYearSnapshots,
      projectSnapshot: (state) => state.projectSnapshot,
      successMessage: (state) => state.successMessage,
      projectWorkorders: (state) => state.projectWorkorders,
      incentiveTypes: (state) => state.incentiveTypes,
    }),
    annualBudgetIncreaseDifference() {

      let annualBudgetIncreaseDifference = 0;

      const projectBudgetValidationResult = this.projectBudgetValidationResults.find((e) => e.id === this.currentAdjustment.fundingSourceId);
      if (projectBudgetValidationResult) {
        annualBudgetIncreaseDifference = projectBudgetValidationResult.totalAnnualBudgetDifference;
      }

      return annualBudgetIncreaseDifference;
    },
    annualBudgetIncreaseTotal() {

      let annualBudgetIncreaseTotal = 0;

      const projectBudgetValidationResult = this.projectBudgetValidationResults.find((e) => e.id === this.currentAdjustment.fundingSourceId);
      if (projectBudgetValidationResult) {
        annualBudgetIncreaseTotal = projectBudgetValidationResult.totalAnnualBudgetIncrease;
      }

      return annualBudgetIncreaseTotal;
    },
    projectBudgetIncreaseTotal() {

      let projectBudgetIncreaseTotal = 0;

      const projectBudgetValidationResult = this.projectBudgetValidationResults.find((e) => e.id === this.currentAdjustment.fundingSourceId);
      if (projectBudgetValidationResult) {
        projectBudgetIncreaseTotal = projectBudgetValidationResult.totalProjectBudgetIncrease;
      }

      return projectBudgetIncreaseTotal;
    },
    fiscalYear() {
      const projectBudgetYear = this.projectBudget.projectBudgetYears.find((e) => e.projectBudgetYearId === this.currentAdjustment.projectBudgetYear);
      return projectBudgetYear.year;
    },
    fundingSource() {

      if (!this.fundingSources) return 'N/A';
      if (!this.currentAdjustment.fundingSourceId) return 'N/A';

      let fundingSourceName = '(N/A)';

      const fundingSource = this.fundingSources.find((e) => {
        if (e.recorD_ID === this.currentAdjustment.fundingSourceId) {
          return true;
        }
        return false;
      });

      if (fundingSource) {
        fundingSourceName = fundingSource.recorD_NAME;
      }

      return fundingSourceName;
    },
    fundingSourceApprovedProjectsBudget() {

      let fundingSourceApprovedProjectsBudget = null;

      const projectBudgetValidationResult = this.projectBudgetValidationResults.find((e) => e.id === this.currentAdjustment.fundingSourceId);
      if (projectBudgetValidationResult) {
        fundingSourceApprovedProjectsBudget = projectBudgetValidationResult.totalApprovedBudget;
      }

      return fundingSourceApprovedProjectsBudget;
    },
    fundingSourceEncumberedTotal() {
      let fundingSourceEncumberedTotal = 0;
      const projectBudgetValidationResult = this.projectBudgetValidationResults.find((e) => e.id === this.currentAdjustment.fundingSourceId);
      if (projectBudgetValidationResult) {
        fundingSourceEncumberedTotal = projectBudgetValidationResult.totalEncumberedFunds;
      }
      return fundingSourceEncumberedTotal;
    },
    fundingSourceRemainingTotal() {

      let fundingSourceRemainingTotal = 0;

      const projectBudgetValidationResult = this.projectBudgetValidationResults.find((e) => e.id === this.currentAdjustment.fundingSourceId);
      if (projectBudgetValidationResult) {
        fundingSourceRemainingTotal = projectBudgetValidationResult.totalApprovedBudgetRemaining;
      }

      return fundingSourceRemainingTotal;
    },
    fundingSourceDifferenceTotal() {

      let fundingSourceDifferenceTotal = 0;

      const projectBudgetValidationResult = this.projectBudgetValidationResults.find((e) => e.id === this.currentAdjustment.fundingSourceId);
      if (projectBudgetValidationResult) {
        fundingSourceDifferenceTotal = projectBudgetValidationResult.fundingSourceDifferenceTotal;
      }

      return fundingSourceDifferenceTotal;
    },
  },
  props: {
    budgetYear: Object,
  },
  data() {
    return {
      currentAdjustment: this.initAdjustment(),
      fundingSourceBudgets: [],
      funds: [],
      historyItems: [],
      projectBudgetValidationResults: [],
      pushingData: false,
      showAlertWarning: false,
      validAdjustment: false,
      validationMessage: '',
    };
  },
  async mounted() {
    this.setExistingFunds(this.budgetYear);
  },
  methods: {
    initAdjustment() {
      return {
        adjustmentToAnnualBudget: null,
        adjustmentToProjectBudget: null,
        fundingSourceId: null,
        projectBudgetYearId: null,
      };
    },
    loadBudgetData() {
      this.$store.dispatch('Project/forceFetchProjectBudget', this.projectBudget.projectId);
      this.$store.dispatch('Project/getProjectSnapshot', this.projectBudget.projectId);
      this.$store.dispatch('Project/getProjectBudgetYearSnapshots', this.projectBudget.projectId);
    },
    loadAnnualBudgetItems() {
      // load history so the the annual budget history component will pick up the latest changes
      this.$store.dispatch('Project/fetchAnnualBudgetHistoryItems', this.budgetYear.id);
    },
    async addAnnualBudgetIncrease(fiscalYear) {
      console.debug('fiscalYear: ', fiscalYear);
      const vm = this;

      vm.pushingData = true;
      vm.$bvModal.hide(`adjustmentConfirmationModal-${vm.budgetYear.year}`);

      // // According to Mindia's comments in https://www.pivotaltracker.com/story/show/180635693,
      // // we should be looking at "Available for Approved Projects"...so this is incorrect?
      // vm.fundingSourceBudgets = vm.fundsForNewProjects();

      // // Call the helper function that validates that the specified funding source
      // // has enough money left in it for the requested increase
      // await vm.validateAnnualBudgetIncrease(vm.budgetYear.year, vm.currentAdjustment);

      // const validationResult = vm.projectBudgetValidationResults.find(
      //   (e) => Number(e.id) === vm.currentAdjustment.fundingSourceId,
      // );

      // if (validationResult.balance >= 0) {

      //   await vm.$store.dispatch('Project/getEncumberedFundsOnApprovedBudgets', vm.budgetYear.year);
      //   await vm.$store.dispatch('Project/getFundingSourceExpendedFunds', vm.budgetYear.year);

      //   if (vm.isAnnualFundingSourceSufficient()) {
      //     vm.$bvModal.hide(`adjustmentModal-${vm.budgetYear.year}`);
      //     vm.$store.dispatch('Project/addAnnualBudgetIncrease', vm.currentAdjustment).then(() => {
      //       vm.pushingData = false;
      //       if (vm.successMessage) {
      //         vm.showSuccessAlert();
      //         vm.loadAnnualBudgetItems();
      //         vm.loadBudgetData();
      //       } else {
      //         vm.showAlertWarning = true;
      //       }
      //     });

      //     vm.currentAdjustment = vm.initAdjustment();
      //     vm.validationMessage = '';

      //   } else {
      //     vm.adjustmentValidationFailed('There are insufficient funds in this funding source to cover the requested change to the Annual Budget. Please make adjustments to amounts being requested, funding sources used, and/or increase the available funds for the funding sources used.');
      //   }

      // } else {
      //   vm.adjustmentValidationFailed('There are insufficient funds in this funding source to cover the requested change to the Project Budget. Please make adjustments to amounts being requested, funding sources used, and/or increase the available funds for the funding sources used.');
      // }

      vm.$bvModal.hide(`adjustmentModal-${vm.budgetYear.year}`);
      vm.$store.dispatch('Project/addAnnualBudgetIncrease', vm.currentAdjustment).then(() => {
        vm.pushingData = false;
        if (vm.successMessage) {
          vm.showSuccessAlert();
          vm.loadAnnualBudgetItems();
          vm.loadBudgetData();
        } else {
          vm.showAlertWarning = true;
        }
      });

      vm.currentAdjustment = vm.initAdjustment();
      vm.validationMessage = '';
    },
    adjustmentValidationFailed(message) {
      this.pushingData = false;
      this.validationMessage = message;
      this.showAlertWarning = true;
    },
    cancelAdjustment() {
      this.showAlertWarning = false;
      this.currentAdjustment = this.initAdjustment();
    },
    async onAddItemClicked(year) {
      await this.validateAnnualBudgetIncrease(year, this.currentAdjustment);
      this.$bvModal.show(`adjustmentConfirmationModal-${year}`);
    },
    onAdjustmentUpdated() {
      // The validation for the annual budget and project budget fields depend
      // on each other, so we manually rerun it whenever they're both filled in
      if (
        this.currentAdjustment.adjustmentToAnnualBudget !== null
        && this.currentAdjustment.adjustmentToProjectBudget !== null
      ) {
        this.$refs.annualBudgetIncreaseForm.validate();
      }
    },
    onAdjustmentValidation(isValid) {
      this.validAdjustment = isValid
        && this.currentAdjustment.fundingSourceId
        && this.currentAdjustment.adjustmentToAnnualBudget
        && this.currentAdjustment.adjustmentToAnnualBudget > 0
        && this.currentAdjustment.adjustmentToProjectBudget !== null
        && this.currentAdjustment.adjustmentToProjectBudget >= 0;
    },
    isAnnualFundingSourceSufficient() {
      const vm = this;
      vm.fundingSourceBudgets = vm.fundsForAnnualBudgets();
      // need to total all amounts for each fundingSource on this AnnualBudget
      const currentYearFundingAmount = vm.getAdjustmentAmountByFundingSource();

      // see if this AnnualBudget puts any FundingSource over it's approved budget
      // and just return the names of any that do exceed their budget
      const fundingSource = vm.getOrInitializeBudget(vm.fundingSourceBudgets,
        currentYearFundingAmount.id);
      const encumberedFunds = vm.getOrInitializeBudget(
        vm.encumberedFundsForApprovedBudgets,
        currentYearFundingAmount.id,
      );
      const expendedFunds = vm.getOrInitializeExpendedFunds(
        vm.fundingSourceExpendedFunds,
        currentYearFundingAmount.id,
      );
      return fundingSource.budget
        - encumberedFunds.budget
        - expendedFunds.total
        - currentYearFundingAmount.amount
        >= 0;
    },
    getAdjustmentAmountByFundingSource() {
      return {
        id: this.currentAdjustment.fundingSourceId,
        amount: this.currentAdjustment.adjustmentToAnnualBudget,
      };
    },
    getOrInitializeBudget(fundingList, fundingSourceId) {
      return fundingList.find((f) => f.id === fundingSourceId) || { budget: 0.0 };
    },
    getOrInitializeExpendedFunds(expendedFundsList, fundingSourceId) {
      return expendedFundsList.find((e) => e.id === fundingSourceId) || { total: 0.0 };
    },
    openAdjustmentModal() {
      this.currentAdjustment.projectBudgetYearId = this.budgetYear.id;
      this.currentAdjustment.projectId = this.projectBudget.projectId;
      this.$bvModal.show(`adjustmentModal-${this.budgetYear.year}`);
    },
  },
};
</script>
